// The Agents page's model saves (#5210, epic #5200) — what one pick on a card's
// `ModelPicker` writes, and everything that answers it.
//
// THE WRITE is `nextRouteRow` (`@developerz/domain`, #5201): the FULL intended row
// built from the stored one plus the change, so a model pick never wipes the key pin
// or the thinking depth it did not touch, and a change leaving no pin axis is a
// DELETE. Every write carries the #5208 precondition — the PUT's `expect`, the
// DELETE's `?expect_updated_at=` — built from the row this page READ, so a save racing
// another writer (a teammate, an AI helper over MCP) is refused 409 rather than
// silently overwriting it.
//
// THE SECOND OPTIMISTIC WRITE IN THE DASHBOARD (after `lib/key-priority.ts`), and it
// still ends in a re-read (`apps/dashboard/CLAUDE.md` § Boundary). The card shows the
// pick the moment it is made, rolls THAT purpose's row back on a refusal — never a
// whole-list snapshot, so a refusal cannot undo a concurrent save on another card —
// and re-reads the list either way: the patch is a preview, the refetch is the answer.
//
// NO GUARDED WRITE OVER A GUESS. Every row this page paints — the pick, and the
// rollback after a refusal, which a 409 has just proved is not what is stored —
// carries no stored version (`UNREAD`). The toast, the refusal notice and a second
// pick are all clickable while the re-read after a write is still in flight, so every
// write first `settleRead`s: with a read in flight or its own row unread, it waits
// for a read that started after it, and builds `before` — the precondition, the
// fields a replay keeps — from that, never from the paint.
//
// UNDO IS GUARDED BY THE VERSION MY SAVE PRODUCED, never by the cache. The toast is
// up for seconds; a teammate's write landing in that window is re-read into the
// cache, and an Undo guarded by the cached version would pass and overwrite THEIR
// pin — the race #5208 exists to refuse. So the restore carries the PUT echo's
// `updated_at_iso` (or `absent` after a DELETE) as its `expect`, and a newer write
// refuses it 409 like any other stale save.
//
// A WRITE GOES TO THE ORG IT WAS MADE IN, OR NOWHERE. `OrgScope` re-keys on an
// org-only URL change WITHOUT unmounting this page, so the Undo toast, a refusal's
// retry and an in-flight save all outlive a switch — and `apiFetch` names the tenant
// (`X-Dz-Account`) from `location` at REQUEST time. An Undo clicked after switching to
// org B would put org A's old row into org B (its `{absent: true}` expect passing
// trivially there). So every intent carries the tenant it was made under
// (`tenantNow`), and `send` refuses one that no longer matches the page —
// `account_changed`, unsent — checked again after its only await, and its cache work
// is keyed to THAT org, never the live one. The org-switch effect below is the second
// layer: it drops the toast and every standing refusal the moment the org changes.
//
// THE ANSWERS, per `lib/route-refusal.ts`:
//   - saved       -> a toast naming what the change also cleared, with Undo;
//   - 409 stale   -> "Changed by @x just now…", and "Apply mine anyway", which REPLAYS
//                    the change over the re-read row with no `expect` — never the old
//                    body, which would wipe what the other writer pinned;
//   - 422 reasoning_refused -> inline, with "Apply and reset thinking depth": the same
//                    change, then the effort cleared;
//   - any other 422 -> its detail inline, no cure; 403 -> the lock state.
// Refusals are held PER PURPOSE: Coder and DevOps both pin `general`, so they share
// one lock and one notice, which is the truth — they share one pin.

import {
  type LlmRoutePurposeAny,
  modelDisplayName,
  type NextRouteRow,
  nextRouteRow,
  type RouteChange,
  type RouteField,
  type RoutePutBody,
  type RouteRow,
} from '@developerz/domain';
import type { QueryClient } from '@tanstack/solid-query';
import { t } from '../i18n';
import { AGENTS_HISTORY_QUERY_KEY } from './agents-history';
import { AGENTS_PREVIEW_QUERY_KEY } from './agents-preview';
import {
  createGuardedSaves,
  type PendingRefusal as GuardedPendingRefusal,
  sameTenant,
  type Tenant,
  tenantNow,
} from './guarded-save';
import {
  deleteRoute,
  llmRoutesQueryKey,
  type RouteExpect,
  type RouteWire,
  upsertOrgRoute,
  upsertRepoRoute,
} from './llmRoutes';
import { routeRefusalOf } from './route-refusal';
import type { ToastAction, ToastTone } from './toast';

export type { Tenant };
export { sameTenant, tenantNow };

/**
 * What a save means to do: one change to the pin, or putting a prior row back (Undo) —
 * and the tenant it was made under, which is the only one it may write.
 */
export type SaveIntent =
  | { readonly kind: 'change'; readonly change: RouteChange; readonly tenant: Tenant }
  | {
      readonly kind: 'restore';
      readonly row: RouteRow | null;
      /** The version the save being undone produced — Undo's precondition. */
      readonly expect: RouteExpect;
      /** The tenant the save being undone wrote to. */
      readonly tenant: Tenant;
    };

/** A refused save, and the intent it refused — what the notice's retry replays. */
export type PendingRefusal = GuardedPendingRefusal<SaveIntent>;

export type ModelSaves = {
  /** True while THIS purpose has a write in flight. */
  readonly isBusy: (purpose: string) => boolean;
  /** The purpose's standing refusal, or null. */
  readonly refusal: (purpose: string) => PendingRefusal | null;
  /** Save one change. Awaitable for tests; resolves on a refusal too (it is held). */
  readonly choose: (purpose: LlmRoutePurposeAny, change: RouteChange) => Promise<void>;
  /** After a 409: replay the refused intent over the re-read row, unconditionally. */
  readonly applyAnyway: (purpose: LlmRoutePurposeAny) => Promise<void>;
  /** After a reasoning refusal: replay it with the thinking depth cleared. */
  readonly applyWithoutEffort: (purpose: LlmRoutePurposeAny) => Promise<void>;
  readonly dismiss: (purpose: string) => void;
};

/** How a replay treats the precondition and the effort. */
type Mode = 'guarded' | 'force' | 'no_effort';

const FIELD_LABEL: Readonly<Record<RouteField, string>> = {
  key_label: 'agents.save.field.key_label',
  provider: 'agents.save.field.provider',
  model: 'agents.save.field.model',
  reasoning_effort: 'agents.save.field.reasoning_effort',
};

/** The four replaceable columns of a stored route. */
export function rowOf(route: RouteWire): RouteRow {
  const { key_label, provider, model, reasoning_effort } = route;
  return { key_label, provider, model, reasoning_effort };
}

/** A row as the PUT takes it: pinned axes present, unpinned ones ABSENT (the edge 400s a null). */
function bodyOf(row: RouteRow): RoutePutBody {
  return {
    ...(row.key_label === null ? {} : { key_label: row.key_label }),
    ...(row.provider === null ? {} : { provider: row.provider }),
    ...(row.model === null ? {} : { model: row.model }),
    reasoning_effort: row.reasoning_effort,
  };
}

const sameBody = (a: RoutePutBody, b: RoutePutBody): boolean =>
  a.key_label === b.key_label &&
  a.provider === b.provider &&
  a.model === b.model &&
  a.reasoning_effort === b.reasoning_effort;

/**
 * The write an intent needs over `current`, or null when it would change nothing
 * (re-choosing the current pin, Automatic on a row with no model, clearing nothing).
 * Pure.
 */
export function planWrite(
  current: RouteRow | null,
  intent: SaveIntent,
  mode: Mode,
): NextRouteRow | null {
  let next: NextRouteRow;
  if (intent.kind === 'change') {
    next = nextRouteRow(current, intent.change);
  } else {
    const row = intent.row;
    const pinsNothing = row === null || (row.key_label ?? row.provider ?? row.model) === null;
    next =
      pinsNothing || row === null
        ? { op: 'delete', cleared: [] }
        : { op: 'put', body: bodyOf(row), cleared: [] };
  }
  if (mode === 'no_effort' && next.op === 'put' && next.body.reasoning_effort !== null) {
    next = {
      op: 'put',
      body: { ...next.body, reasoning_effort: null },
      cleared: next.cleared.includes('reasoning_effort')
        ? next.cleared
        : [...next.cleared, 'reasoning_effort'],
    };
  }
  if (next.op === 'delete') return current === null ? null : next;
  return current !== null && sameBody(next.body, bodyOf(current)) ? null : next;
}

/** The toast for a saved write. Reused by `repo-route-save.ts` — one-impl. */
export function savedMessage(intent: SaveIntent, next: NextRouteRow): string {
  const head =
    intent.kind === 'restore'
      ? t('agents.save.undone')
      : next.op === 'put' && next.body.model !== undefined
        ? t('agents.save.saved', { model: modelDisplayName(next.body.model) })
        : t('agents.save.automatic');
  if (next.cleared.length === 0) return head;
  const fields = next.cleared.map((f) => t(FIELD_LABEL[f])).join(', ');
  return t('agents.save.with_cleared', { message: head, fields });
}

/** The org route for `purpose` in a cached list. */
const orgRoute = (rows: readonly RouteWire[] | undefined, purpose: string): RouteWire | undefined =>
  rows?.find((r) => r.scope === 'org' && r.purpose === purpose);

/**
 * The version a painted row carries: none. No stored row has it, so `settleRead`
 * can tell a paint from a read even after a cancelled fetch reverts the cache.
 */
const UNREAD = '';

/**
 * The #5208 precondition: a restore's own (the version its save produced), else the
 * row this page read; none on a forced replay. Reused by `repo-route-save.ts` —
 * one-impl: the rule is the same regardless of which scope the row lives at.
 */
export function expectFor(
  before: RouteWire | undefined,
  intent: SaveIntent,
  mode: Mode,
): RouteExpect | undefined {
  if (mode === 'force') return undefined;
  if (intent.kind === 'restore') return intent.expect;
  return before === undefined ? { absent: true } : { updated_at_iso: before.updated_at_iso };
}

/** The row the card paints while the write is in flight; `undefined` = the pin is gone. */
function pendingRow(
  purpose: string,
  before: RouteWire | undefined,
  next: NextRouteRow,
): RouteWire | undefined {
  if (next.op === 'delete') return undefined;
  return {
    id: before?.id ?? `pending:${purpose}`,
    scope: 'org',
    scope_ref: before?.scope_ref ?? '',
    purpose,
    key_label: next.body.key_label ?? null,
    provider: next.body.provider ?? null,
    model: next.body.model ?? null,
    reasoning_effort: next.body.reasoning_effort,
    updated_at_iso: UNREAD,
  };
}

/**
 * Wait until the cached list is one a guarded write may be built over: no read in
 * flight, and `purpose`'s row not a paint. False when a finished read still left it
 * a paint (the read failed, or the query has no enabled observer to refetch it).
 * `reread` forces one read regardless — a replay's: the refusal it answers is the
 * server contradicting the cache, and a refused FIRST pin's rollback removes the row,
 * so no `UNREAD` marker is left to say so.
 *
 * The FIRST read restarts any in-flight one, so what lands started after every write
 * this page already saw answered. A later pass JOINS instead: our read was cancelled
 * only by someone else restarting one — which started after ours did — and a second
 * restart would cancel theirs back, forever. `cancelQueries` is not an option: it
 * reverts the cache to where the fetch started, which may be a paint.
 */
async function settleRead(
  client: QueryClient,
  queryKey: readonly unknown[],
  purpose: string,
  reread: boolean,
): Promise<boolean> {
  const unread = (): boolean =>
    orgRoute(client.getQueryData(queryKey), purpose)?.updated_at_iso === UNREAD;
  let restart = true;
  while ((restart && reread) || client.isFetching({ queryKey }) > 0 || unread()) {
    if (!restart && client.isFetching({ queryKey }) === 0) return false;
    await client.refetchQueries({ queryKey }, { cancelRefetch: restart });
    restart = false;
  }
  return true;
}

/** Where a route write lands — reused by `repo-route-save.ts` so its writes go
 * through the SAME `writeRoute` (one-impl) rather than a second PUT/DELETE
 * dispatch. */
export type RouteTarget =
  | { readonly scope: 'org' }
  | { readonly scope: 'repo'; readonly repoId: string };

/**
 * Send one planned write with its precondition, at `target`. Throws the API's
 * refusal; resolves to the version the write produced — the PUT echo's
 * `updated_at_iso`, or `absent` after a DELETE — which is Undo's precondition,
 * never a row to paint.
 */
export async function writeRoute(
  target: RouteTarget,
  purpose: LlmRoutePurposeAny,
  next: NextRouteRow,
  expect: RouteExpect | undefined,
  token: string | null,
): Promise<RouteExpect> {
  if (next.op === 'put') {
    const body = expect === undefined ? next.body : { ...next.body, expect };
    const saved =
      target.scope === 'org'
        ? await upsertOrgRoute(purpose, body, token)
        : await upsertRepoRoute(target.repoId, purpose, body, token);
    return { updated_at_iso: saved.updated_at_iso };
  }
  // A DELETE's precondition is a version; `absent` cannot arise: there is nothing to
  // delete, and an Undo's DELETE follows the PUT that created the row, so it carries
  // that PUT's version.
  const version =
    expect !== undefined && 'updated_at_iso' in expect ? expect.updated_at_iso : undefined;
  await deleteRoute(
    target.scope === 'org'
      ? { scope: 'org', scope_ref: '', purpose }
      : { scope: 'repo', scope_ref: target.repoId, purpose },
    token,
    version,
  );
  return { absent: true };
}

/**
 * Call in a component body: the org-switch effect needs an owner to live and be
 * disposed in.
 */
export function createModelSaves(input: {
  readonly client: QueryClient;
  readonly token: string | null;
  readonly notify: (message: string, tone?: ToastTone, action?: ToastAction) => void;
  /** Take the page's toast down — an org switch strands its Undo in the other org. */
  readonly dismissNotice: () => void;
}): ModelSaves {
  // The lock, the per-purpose refusal store and the org-switch cleanup effect
  // are `guarded-save.ts`'s — one-impl, shared with `agent-mode-save.ts` and
  // `repo-route-save.ts` (see that file's header for the round-3 bug it fixes).
  const guarded = createGuardedSaves<SaveIntent>({
    fallback: t('agents.save.failed'),
    dismissNotice: input.dismissNotice,
    // Keyed to the INTENT's org, never the live one: a re-read of the old
    // org's key after a switch would run its queryFn under the NEW org's
    // `X-Dz-Account`, caching org B's rows as org A's — `run` already skips
    // this once the page has moved.
    reread: async (intent) => {
      const queryKey = llmRoutesQueryKey(input.token, intent.tenant.account);
      await input.client.refetchQueries({ queryKey });
      void input.client.invalidateQueries({ queryKey: [AGENTS_HISTORY_QUERY_KEY] });
      void input.client.invalidateQueries({ queryKey: [AGENTS_PREVIEW_QUERY_KEY] });
    },
  });

  /**
   * Swap `purpose`'s org row in the cached list (`undefined` removes it). The row goes
   * in {@link UNREAD}: the write's echo is never painted (§ Boundary), so the stored
   * version is only known once the list is re-read. (Its version IS carried — into
   * Undo's precondition, not into this cache.)
   */
  const paint = (
    queryKey: readonly unknown[],
    purpose: string,
    row: RouteWire | undefined,
  ): void => {
    input.client.setQueryData<readonly RouteWire[]>(queryKey, (rows) => {
      const rest = (rows ?? []).filter((r) => !(r.scope === 'org' && r.purpose === purpose));
      return row === undefined ? rest : [...rest, { ...row, updated_at_iso: UNREAD }];
    });
  };

  /**
   * Undo for a saved CHANGE: put the prior row back, guarded by `saved` — the version
   * that save produced — so a write that landed after it refuses the Undo as stale.
   * An undo offers none.
   */
  const undoOf = (
    purpose: LlmRoutePurposeAny,
    intent: SaveIntent,
    before: RouteWire | undefined,
    saved: RouteExpect,
  ): ToastAction | undefined => {
    if (intent.kind !== 'change') return undefined;
    const row = before === undefined ? null : rowOf(before);
    const restore: SaveIntent = { kind: 'restore', row, expect: saved, tenant: intent.tenant };
    return { label: t('agents.save.undo'), run: () => void send(purpose, restore, 'guarded') };
  };

  const send = (
    purpose: LlmRoutePurposeAny,
    intent: SaveIntent,
    mode: Mode,
    reread = false,
  ): Promise<void> =>
    guarded.run(purpose, intent, async (ctx) => {
      // Every cache touch below is keyed to the intent's org, never the live
      // one: a rollback after a switch must not paint org A's row into org
      // B's list.
      const queryKey = llmRoutesQueryKey(input.token, intent.tenant.account);
      if (!(await settleRead(input.client, queryKey, purpose, reread))) {
        ctx.refuse({ kind: 'failed', detail: null });
        return;
      }
      // No `await` from here to the request: `apiFetch` reads `location` before its
      // first await, so the tenant checked is the tenant sent, and nothing lands
      // between the settled read and the paint.
      if (ctx.refuseMoved()) return;
      const before = orgRoute(input.client.getQueryData(queryKey), purpose);
      const next = planWrite(before === undefined ? null : rowOf(before), intent, mode);
      ctx.clearRefusal();
      if (next === null) return;
      paint(queryKey, purpose, pendingRow(purpose, before, next));
      let saved: RouteExpect;
      try {
        saved = await writeRoute(
          { scope: 'org' },
          purpose,
          next,
          expectFor(before, intent, mode),
          input.token,
        );
      } catch (err) {
        paint(queryKey, purpose, before);
        // Its notice would stand on the other org's card, retrying into it.
        ctx.refuse(routeRefusalOf(err));
        return;
      }
      // Landed after a switch: its toast (and Undo) would sit on the other org's page.
      if (ctx.moved()) return;
      input.notify(savedMessage(intent, next), 'success', undoOf(purpose, intent, before, saved));
    });

  /**
   * Replay the purpose's refused intent in `mode`; a no-op with nothing refused.
   *
   * RE-READ FIRST, always (`reread`): the notice is up while the re-read after the
   * refusal may not even have started, so the retry is built over what is stored now
   * — never the rolled-back row read BEFORE the other writer's save, whose fields
   * "Apply mine anyway" would re-send (the D6 wipe) and whose version the reset would
   * 409 on.
   */
  const replay = async (purpose: LlmRoutePurposeAny, mode: Mode): Promise<void> => {
    const pending = guarded.refusal(purpose);
    if (pending === null) return;
    await send(purpose, pending.intent, mode, true);
  };

  return {
    isBusy: guarded.isBusy,
    refusal: guarded.refusal,
    choose: (purpose, change) =>
      send(purpose, { kind: 'change', change, tenant: tenantNow() }, 'guarded'),
    applyAnyway: (purpose) => replay(purpose, 'force'),
    applyWithoutEffort: (purpose) => replay(purpose, 'no_effort'),
    dismiss: guarded.dismiss,
  };
}
